import { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { apiFetch, apiImageUrl } from '../utils/streamApi';
import ReviewView from '../components/ReviewView';
import EvidenceStrip from '../components/EvidenceStrip';
import { getAlertPollMs, getIdleReloadMs } from '../utils/alertPollConfig';
import { downloadAlertLogs } from '../utils/alertLogExport';
import { cacheAlertsToDevice, enrichOfflineDetail } from '../utils/offlineCacheSync';
import useIsMobile from '../utils/useIsMobile';
import { fetchUnread, markAlertsSeen, stampFromFilename } from '../utils/unreadAlerts';
import ConfirmDialog from '../components/ConfirmDialog';
import CamOfflineModal from '../components/CamOfflineModal';
import '../css/parvekshak.css';
import '../css/dashboard.css';
import { productHas } from '../utils/entitlements';
import { FC_REASONS, isFaceCoverAlert, reasonsFromInfo } from '../utils/faceCover';

const PER_PAGE = 20;

const EV_SECTIONS = [
  { label: 'Rule', fields: [
    ['ev_rule_trigger',      'Triggered by'],
    ['ev_rule_block_reason', 'Blocked by'],
  ]},
  { label: 'Detection', fields: [
    ['ev_yolo_conf_best', 'YOLO conf best'],
    ['ev_yolo_conf_last', 'YOLO conf last'],
  ]},
  { label: 'Face', fields: [
    ['ev_face_large',     'Face ≥40px'],
    ['ev_face_small',     'Face ≥10px'],
    ['ev_face_w',         'Face w'],
    ['ev_face_h',         'Face h'],
    ['ev_face_det_score', 'Det score'],
    ['ev_face_quality',   'Quality'],
    ['ev_face_frames',    'Frames'],
  ]},
  { label: 'Skeleton', fields: [
    ['ev_skeleton',             'Skeleton'],
    ['ev_skeleton_frame_count', 'Skel frames'],
    ['ev_pose_frame_count',     'Pose frames'],
    ['ev_kp_conf_count',        'KP count'],
    ['ev_kp_conf_mean',         'KP mean'],
    ['ev_kp_shoulder_conf',     'Shoulder'],
    ['ev_kp_elbow_conf',        'Elbow'],
    ['ev_kp_hip_conf',          'Hip'],
    ['ev_kp_knee_conf',         'Knee'],
    ['ev_kp_ankle_conf',        'Ankle'],
    ['ev_segment_score',        'Segment'],
    ['ev_pose_confirmed_track', 'RTMPose ran'],
  ]},
  { label: 'CLIP', fields: [
    ['ev_clip_human',  'Human'],
    ['ev_clip_score',  'Score'],
    ['ev_clip_source', 'Source'],
  ]},
  { label: 'Clothing', fields: [
    ['ev_clothing', 'Clothing'],
  ]},
  { label: 'Animal', condition: ev => ev.ev_animal_check_ran === true || ev.ev_animal_rejected === true, fields: [
    ['ev_animal_check_ran',  'Check ran'],
    ['ev_animal_rejected',   'Rejected'],
    ['ev_animal_best_conf',  'Best conf seen'],
    ['ev_animal_class',      'Class'],
    ['ev_animal_conf',       'Reject conf'],
    ['ev_animal_mode',       'Mode'],
  ]},
  { label: 'Motion', fields: [
    ['ev_track_path_px', 'Track path (px)'],
    ['ev_path_px',       'Path (px)'],
    ['ev_net_px',        'Net (px)'],
    ['ev_net_ratio',     'Net ratio'],
    ['ev_fast_track',    'Fast-track'],
  ]},
];

function fmtEvVal(v) {
  if (v === true)  return <span style={{ color: '#22c55e' }}>✅</span>;
  if (v === false) return <span style={{ color: '#ef4444' }}>❌</span>;
  if (v === null || v === undefined) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  if (typeof v === 'number') return <span>{Number.isInteger(v) ? v : v.toFixed(3)}</span>;
  return <span>{String(v)}</span>;
}

function EvTiles({ ev }) {
  if (!ev) return null;
  const knownKeys = new Set(EV_SECTIONS.flatMap(s => s.fields.map(f => f[0])));
  const extraKeys = Object.keys(ev).filter(k => !knownKeys.has(k));
  const tileStyle = {
    background: 'var(--surface-alt)', borderRadius: 8,
    padding: '8px 10px', marginBottom: 6,
  };
  return (
    <>
      {EV_SECTIONS.map(sec => {
        if (sec.condition && !sec.condition(ev)) return null;
        const rows = sec.fields.filter(([k]) => ev[k] !== null && ev[k] !== undefined);
        if (!rows.length) return null;
        return (
          <div key={sec.label} style={tileStyle}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
              {sec.label}
            </div>
            {rows.map(([k, label]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 2 }}>
                <span style={{ color: 'var(--text-muted)' }}>{label}</span>
                {fmtEvVal(ev[k])}
              </div>
            ))}
          </div>
        );
      })}
      {extraKeys.length > 0 && (
        <div style={tileStyle}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Other</div>
          {extraKeys.map(k => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 2 }}>
              <span style={{ color: 'var(--text-muted)' }}>{k}</span>
              {fmtEvVal(ev[k])}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

const TYPE_META = {
  unknown:        { label: 'Unknown',   cls: 'badge-unknown' },
  banned:         { label: 'Banned',    cls: 'badge-banned'  },
  instant:        { label: 'Detected',  cls: 'badge-instant' },
  motion:         { label: 'Motion',    cls: 'badge-motion'  },
  zone_watch:     { label: 'Loitering', cls: 'badge-instant' },
  zone_intrusion: { label: 'Intrusion', cls: 'badge-instant' },
  after_hours:    { label: 'After hours', cls: 'badge-instant' },
  camera_blocked: { label: 'Blocked',   cls: 'badge-motion'  },
  face_cover:     { label: 'Face covered', cls: 'badge-unknown' },
};

// An alert id is <detector>_<Camera_Name>_YYYYMMDD_HHMMSS (main.py builds it that
// way). Several detector names carry an underscore of their own — zone_watch,
// zone_intrusion, object_count, seat_occupancy, after_hours, camera_blocked — and
// reading the prefix as [a-z]+ cut every one of them at the first underscore:
// "zone_watch_Front_Gate_..." came back as type "zone" on a camera called
// "watch_Front_Gate". No badge matched, and the camera column was wrong.
//
// Matched longest-first so zone_intrusion cannot be swallowed by a shorter name
// sharing its start. The old pattern stays as the fallback, so a detector added
// later still parses as something rather than vanishing from the list.
const DETECTOR_PREFIXES = [
  'zone_intrusion', 'seat_occupancy', 'camera_blocked', 'object_count',
  'after_hours', 'zone_watch', 'face_cover', 'unattended', 'instant', 'unknown', 'banned',
  'helmet', 'motion', 'crowd', 'known', 'arms', 'fire', 'fall',
].sort((a, b) => b.length - a.length);

function parseName(fname) {
  const base  = fname.replace(/\.(jpg|jpeg|png|mp4)$/i, '');
  const known = DETECTOR_PREFIXES.find(p => base.toLowerCase().startsWith(`${p}_`));
  const m     = known
    ? base.slice(known.length + 1).match(/^(.+)_(\d{8})_(\d{6})$/)
    : base.match(/^([a-z]+)_(.+)_(\d{8})_(\d{6})$/i);
  if (m) {
    const [type, cam, date, time] = known ? [known, m[1], m[2], m[3]]
                                          : [m[1], m[2], m[3], m[4]];
    const y = date.slice(0,4), mo = date.slice(4,6), d = date.slice(6,8);
    const h = time.slice(0,2), mi = time.slice(2,4), s = time.slice(4,6);
    return { type, cam, date: `${d}/${mo}/${y}`, time: `${h}:${mi}:${s}`, dateKey: date };
  }
  return { type: known || base.split('_')[0] || 'unknown',
           cam: null, date: '', time: '', dateKey: '' };
}

function AuthImg({ path, alt, className, onClick }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    apiFetch(path)
      .then(r => r.ok ? r.blob() : null)
      .then(blob => { if (blob) { url = URL.createObjectURL(blob); setSrc(url); } })
      .catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [path]);
  if (!src) return <div className="alert-card-thumb-placeholder">📷</div>;
  return <img src={src} alt={alt} className={className} onClick={onClick} />;
}

function FacePreviewModal({ alertId, filename, onClose, onAddKnown, onAddBan }) {
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    apiFetch(`/alerts/${encodeURIComponent(alertId)}/face/meta`)
      .then(r => r.ok ? r.json() : null)
      .then(d => setMeta(d))
      .catch(() => {});
  }, [alertId]);

  const facePath = `/alerts/faces/${alertId}_face.jpg`;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 20001, background: 'rgba(0,0,0,0.65)',
      backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14,
          padding: 24, width: '100%', maxWidth: 340, boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 15, color: 'var(--text)' }}>Detected Face</h3>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none',
            color: 'var(--text-muted)', fontSize: 18, cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ width: 180, height: 180, margin: '0 auto 16px', borderRadius: 12,
          overflow: 'hidden', background: 'var(--surface-alt)', display: 'flex',
          alignItems: 'center', justifyContent: 'center' }}>
          <AuthImg path={facePath} alt="face crop" className="face-preview-img" />
        </div>
        {meta && (
          <div style={{ marginBottom: 16, fontSize: 13, color: 'var(--text-muted)',
            display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px' }}>
            <span>Det score</span><strong style={{ color: 'var(--text)' }}>{meta.det_score}</strong>
            <span>Sharpness</span><strong style={{ color: 'var(--text)' }}>{meta.sharpness}</strong>
            <span>Camera</span><strong style={{ color: 'var(--text)' }}>{meta.camera}</strong>
            <span>Time</span><strong style={{ color: 'var(--text)' }}>{meta.timestamp?.replace('T', ' ')}</strong>
          </div>
        )}
        {/* The crop is now saved whenever a face was CAPTURED, so this modal also opens
            on faces that failed the enrolment bar — 47px, turned away, blurred. Seeing
            those is the point; enrolling them is not. `meta` is written only alongside
            the embedding, i.e. only when the face is enrollable, so it is the honest
            signal for whether these actions can work. Without this gate the buttons
            would be offered for a face with no embedding: the save fails server-side,
            and a user who retries under a different name splits that identity in the
            known-faces DB. */}
        {/* Product may leave known / banned people out (CLIENT_TYPE) — no buttons then. */}
        {!productHas('known-faces-add') && !productHas('banned') ? null : meta ? (
          <div style={{ display: 'flex', gap: 8 }}>
            {productHas('known-faces-add') && <button onClick={onAddKnown}
              style={{ flex: 1, padding: '10px 0', background: '#2ecc71', color: '#fff',
                border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              👤 Add to Known
            </button>}
            {productHas('banned') && <button onClick={onAddBan}
              style={{ flex: 1, padding: '10px 0', background: '#e74c3c', color: '#fff',
                border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              🚫 Add to Ban
            </button>}
          </div>
        ) : (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center',
                        padding: '8px 4px', lineHeight: 1.5 }}>
            Shown for reference only — this face was too small or turned too far away to
            enrol, so it can’t be added to Known or Ban.
          </div>
        )}
      </div>
    </div>
  );
}

const MEDIA_ERR = { 1: 'aborted', 2: 'network', 3: 'decode', 4: 'unsupported' };

// The native video-controls "Download" menu item can't save auth'd clips —
// on the Android WebView it just re-navigates to the raw .mp4 URL (no file
// ever lands in Downloads), and on HLS/blob sources browsers hide it
// outright. Fetch the bytes ourselves (auth header via apiFetch handles
// tokens) and hand them off through the same bridge used for CSV/JSON
// exports (see downloadFile in DetectionsPage.jsx). Works for alert images
// as well as video clips — both are just bytes + a filename.
async function fetchAlertBlob(path) {
  const res = await apiFetch(path);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.blob();
}

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

async function fetchAndSaveFile(path, filename, blob) {
  blob = blob || await fetchAlertBlob(path);
  if (window.FlutterDownload) {
    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload  = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    window.FlutterDownload.postMessage(JSON.stringify({ filename, mime: blob.type || 'application/octet-stream', data: base64 }));
    return;
  }
  if (isIOS()) {
    // Safari ignores <a download> for blob: URLs — the click just navigates
    // to the raw content. In a home-screen PWA there's no browser chrome at
    // all, so that leaves the user stuck on a full-bleed video/image with no
    // way back. Route through the share sheet instead (Save Video/Image or
    // Save to Files); only fall back to opening a tab if sharing is
    // unavailable, since a real Safari tab at least has a close/back button.
    if (navigator.share) {
      const file = new File([blob], filename, { type: blob.type || 'application/octet-stream' });
      if (!navigator.canShare || navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file] });
          return;
        } catch (err) {
          if (err?.name === 'AbortError') return;
        }
      }
    }
    window.open(URL.createObjectURL(blob), '_blank');
    return;
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// PWA browsers (desktop/iOS/Android Chrome tab) support the Web Share API
// for files directly, delegating to the OS share sheet. The Android
// WebView, though also Chromium, does NOT implement navigator.share() — so
// there we go through the FlutterShare bridge (base64 bytes → MainActivity
// opens a native ACTION_SEND chooser via FileProvider). Falls back to a
// plain download (so the user can share manually) if neither path is
// available, or resolves 'cancelled' on user-dismissed share sheets so
// callers don't show a spurious error toast.
async function shareOrDownloadFile(path, filename, mime) {
  const blob = await fetchAlertBlob(path);
  if (window.FlutterShare) {
    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload  = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    window.FlutterShare.postMessage(JSON.stringify({ filename, mime: blob.type || mime, data: base64 }));
    return 'shared';
  }
  const file = new File([blob], filename, { type: blob.type || mime });
  if (navigator.share && (!navigator.canShare || navigator.canShare({ files: [file] }))) {
    try {
      await navigator.share({ files: [file] });
      return 'shared';
    } catch (err) {
      if (err?.name === 'AbortError') return 'cancelled';
    }
  }
  await fetchAndSaveFile(path, filename, blob);
  return 'downloaded';
}

// Flat, transparent-background icons for the card action row — emoji glyphs
// (▶️ 📤 etc.) render as fixed-color bitmaps on most platforms, ignoring CSS
// `color`, so they can't match the buttons' outline/hover theming. These use
// currentColor instead.
const ICON_PROPS = { width: 16, height: 16, viewBox: '0 0 24 24', 'aria-hidden': true };
function PlayIcon() {
  return <svg {...ICON_PROPS} fill="currentColor"><path d="M8 5v14l11-7z" /></svg>;
}
function TrashIcon() {
  return (
    <svg {...ICON_PROPS} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" />
    </svg>
  );
}
function DownloadIcon() {
  return (
    <svg {...ICON_PROPS} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3v12" /><path d="M7 10l5 5 5-5" /><path d="M5 21h14" />
    </svg>
  );
}
function FaceIcon() {
  return (
    <svg {...ICON_PROPS} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="4" /><path d="M4 20c0-4.4 3.6-7 8-7s8 2.6 8 7" />
    </svg>
  );
}
function ShareIcon() {
  return (
    <svg {...ICON_PROPS} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
      <path d="M8.6 13.5l6.8 4" /><path d="M15.4 6.5l-6.8 4" />
    </svg>
  );
}
function MoreIcon() {
  return (
    <svg {...ICON_PROPS} fill="currentColor">
      <circle cx="12" cy="5" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="12" cy="19" r="1.8" />
    </svg>
  );
}

function AuthVideo({ path, poster, className, style, autoPlay }) {
  const [src,     setSrc]     = useState(null);
  const [errored, setErrored] = useState(null);
  const [playing, setPlaying] = useState(!!autoPlay);
  const ref = useRef(null);

  // Resolve the streaming URL only once the user chooses to play (or autoPlay).
  // Mounting a live <video> for every tile exhausts the browser's concurrent
  // video-decoder pool (~75 in Chrome) → tiles fail with MEDIA_ERR_DECODE.
  useEffect(() => {
    if (!playing) return;
    setErrored(null);
    setSrc(null);
    apiImageUrl(path).then(url => setSrc(url));
  }, [path, playing]);

  // iOS Safari: autoPlay attribute alone doesn't work when the src is set
  // asynchronously (gesture timer expires before the URL resolves).
  // Call play() programmatically once the src lands on the element.
  useEffect(() => {
    if (src && ref.current) ref.current.play().catch(() => {});
  }, [src]);

  if (errored) return (
    <div ref={ref} className="alert-card-thumb-placeholder" style={{ flexDirection: 'column', gap: 4 }}>
      <span>🎬</span>
      <span style={{ fontSize: 10, color: '#ef4444', fontWeight: 600 }}>{errored}</span>
    </div>
  );

  // Idle tile: lightweight poster image with a ▶ overlay — no <video> mounted.
  if (!playing) return (
    <div ref={ref} style={{ position: 'relative', cursor: 'pointer', ...style }}
         onClick={() => setPlaying(true)}>
      {poster
        ? <AuthImg path={poster} alt="" className={`${className || ''} video-poster`.trim()} />
        : <div className="alert-card-thumb-placeholder">🎬</div>}
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', background: 'rgba(0,0,0,0.25)', pointerEvents: 'none' }}>
        <span style={{ fontSize: 34, color: '#fff', textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>▶</span>
      </div>
    </div>
  );

  if (!src) return <div ref={ref} className="alert-card-thumb-placeholder">⏳</div>;
  return <video ref={ref} src={src} className={className} style={style}
    controls controlsList="nodownload" preload="metadata" playsInline
    onError={e => {
      const code = e.target.error?.code;
      const label = MEDIA_ERR[code] || `err${code ?? '?'}`;
      console.warn('[VIDEO FAIL]', path, label, e.target.error?.message);
      setErrored(label);
    }} />;
}

function Toast({ msg, type }) {
  if (!msg) return null;
  return <div className={`pk-toast ${type}`}>{msg}</div>;
}

function VerdictBadge({ verdict }) {
  if (!verdict) return null;
  const map = {
    correct:        { text: '✓ Correct',    bg: 'rgba(34,197,94,0.15)',  color: '#22c55e' },
    false_alert:    { text: '✗ False',      bg: 'rgba(239,68,68,0.15)',  color: '#ef4444' },
    wrong_unknown:  { text: '⚠ Wrong ID',   bg: 'rgba(245,158,11,0.15)', color: '#f59e0b' },
    wrong_banned:   { text: '⚠ Wrong Ban',  bg: 'rgba(245,158,11,0.15)', color: '#f59e0b' },
    missed_banned:  { text: '⚠ Missed Ban', bg: 'rgba(245,158,11,0.15)', color: '#f59e0b' },
  };
  const s = map[verdict] || { text: verdict, bg: 'rgba(255,255,255,0.08)', color: 'var(--text-muted)' };
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
      background: s.bg, color: s.color, letterSpacing: '0.03em' }}>
      {s.text}
    </span>
  );
}

// Full View labelling set for FIRED alerts. Two verdicts, deliberately: a third one
// splits "false" across two filters, and every export and tuning pass then has to
// remember to union them. It also went unlabelled more often than it was used.
//
// The distinction the old third verdict carried is NOT lost — "wrong object fired but
// a human WAS there" is now the `wrongobj` reason below. That case must still be
// excluded when tuning a confidence bar, because the detection was right and only the
// box was wrong: one such alert carried 14 keypoints and a person in the clip, with a
// snapshot from 15.6s earlier. Counting it against a threshold would be measuring the
// snapshot, not the detector. Filter on false_reason for that, not on verdict.
const ALERT_VERDICTS = [
  { verdict: 'correct',             label: '✓ Correct',   key: 'c', colour: null },
  { verdict: 'false_alert',         label: '✗ False',     key: 'f', colour: '#ef4444', needsReason: true },
];

const FALSE_REASONS = [
  { value: 'fol',  label: '🌿 Foliage' },
  { value: 'rain', label: '🌧 Rain / Fog' },
  { value: 'ins',  label: '🦟 Insect' },
  { value: 'car',  label: '🚗 Headlights' },
  { value: 'aml',  label: '🐾 Animal' },
  { value: 'fan',  label: '💨 Fan / Rotating' },
  { value: 'sta',  label: '🗿 Static Object' },
  { value: 'shd',  label: '🌑 Shadow' },
  // Replaces the old false_trigger_human verdict. The alert was wrong, but a person
  // WAS in frame — exclude these when tuning a confidence bar.
  { value: 'wrongobj', label: '⚠ Wrong box (person was there)' },
  { value: 'oth',  label: '❓ Other' },
];

// Mirror of _IMG_EXPORT_MAX on the server. Duplicated deliberately: the server is
// the authority and refuses anything past it; this copy only spares the user a
// round trip and a download that was never going to arrive.
const IMG_EXPORT_MAX = 200;

// Save a Blob the browser already holds. downloadFile in alertLogExport handles
// text and base64-encodes it for the Android shell, which would corrupt a zip —
// so binary gets its own path.
function downloadFile2(name, blob) {
  const url = URL.createObjectURL(blob);
  const a   = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

function AlertLogModal({ data, filename, onClose }) {
  // Face-cover alerts carry their own reasons and verdicts in the info payload;
  // every other alert keeps FALSE_REASONS and the Wrong ID button, as before.
  const reasonOpts = reasonsFromInfo(data, FALSE_REASONS);
  const allowsWrongId = !data.verdicts || data.verdicts.includes('wrong_unknown');
  const fmt = (ts) => ts ? new Date(ts * 1000).toLocaleString() : '—';
  const pct = (v)  => v  != null ? `${Math.round(v * 100)}%` : '—';
  const t = data.telemetry || {};

  const [verdict,      setVerdict]      = useState(data.verdict || null);
  const [falseReason,  setFalseReason]  = useState(data.false_reason || '');
  const [saving,       setSaving]       = useState(false);
  const [saveMsg,      setSaveMsg]      = useState(null);
  const [showFalseOpts, setShowFalseOpts] = useState(false);

  const saveVerdict = async (v, reason = null) => {
    setSaving(true); setSaveMsg(null);
    try {
      const res = await apiFetch(`/alerts/${filename}/verdict`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ verdict: v, false_reason: reason }),
      });
      if (res.ok) {
        setVerdict(v);
        setFalseReason(reason || '');
        // Say what the label actually DID. Only 4 of 65 stored records carried a
        // verdict, and the likeliest reason is that marking one changed nothing
        // anybody could see — the same bush alerted again the next night. The
        // backend now teaches scene memory for that spot and returns what it did.
        let learned = null;
        try { learned = (await res.json())?.learned; } catch { /* older backend */ }
        setSaveMsg({ ok: true, text: learned ? `✓ ${learned}` : '✓ Saved' });
      } else {
        setSaveMsg({ ok: false, text: 'Failed' });
      }
    } catch { setSaveMsg({ ok: false, text: 'Error' }); }
    // Longer than the old 2.5s: "siren off for this spot from now on" is the
    // point of pressing the button and is worth actually reading.
    finally { setSaving(false); setTimeout(() => setSaveMsg(null), 6000); }
  };

  const verdictBadge = v => {
    if (!v || v === 'Pending') return <span style={{ color: 'var(--text-muted)' }}>Pending</span>;
    if (v === 'correct')       return <span style={{ color: '#22c55e', fontWeight: 600 }}>✓ Correct</span>;
    if (v === 'false_alert')   return <span style={{ color: '#ef4444', fontWeight: 600 }}>✗ False Alert</span>;
    return <span style={{ color: '#f59e0b', fontWeight: 600 }}>{v.replace(/_/g, ' ')}</span>;
  };

  const rows = [
    { label: 'Alert ID',  value: data.id },
    { label: 'Time',      value: fmt(data.ts) },
    { label: 'Camera',    value: data.cam_name || '—' },
    { label: 'Type',      value: data.label || '—' },
    { label: 'Verdict',   value: verdictBadge(verdict) },
  ];

  const summaryRows = [
    t.face_name && t.face_name !== 'Unknown' && { label: 'Face Match',  value: `${t.face_name} (${pct(t.face_score)})` },
    t.ban_name  && t.ban_name  !== 'Unknown' && { label: 'Ban Match',   value: `${t.ban_name} (${pct(t.banned_score)})` },
    t.in_frame_secs != null                  && { label: 'In Frame',    value: `${Number(t.in_frame_secs).toFixed(1)}s` },
    t.skeleton_score != null                 && { label: 'Skel Score',  value: Number(t.skeleton_score).toFixed(2) },
    t.alert_rule                             && { label: 'Rule',        value: t.alert_rule },
    t.timer != null                          && { label: 'Timer',       value: `${Number(t.timer).toFixed(1)}s` },
  ].filter(Boolean);

  // Face candidates — sorted by score descending
  const candidates = t.known_candidates && typeof t.known_candidates === 'object'
    ? Object.entries(t.known_candidates).sort((a, b) => b[1] - a[1])
    : [];

  const download = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `${filename.replace(/\.(jpg|jpeg|png)$/i, '')}_log.json`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  const Row = ({ label, value }) => (
    <div style={{ display: 'flex', gap: 8, padding: '5px 0',
      borderBottom: '1px solid var(--border)', alignItems: 'flex-start' }}>
      <span style={{ fontSize: 12, color: 'var(--text-muted)', flexShrink: 0, width: 120 }}>{label}</span>
      <span style={{ fontSize: 12, color: 'var(--text)', fontWeight: 500, wordBreak: 'break-all' }}>{value}</span>
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 20001, background: 'rgba(0,0,0,0.65)',
      backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14,
          padding: 24, width: '100%', maxWidth: 480, maxHeight: '85vh',
          overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 15, color: 'var(--text)' }}>📋 Alert Log</h3>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none',
            color: 'var(--text-muted)', fontSize: 18, cursor: 'pointer' }}>✕</button>
        </div>

        <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
          textTransform: 'uppercase', letterSpacing: '0.06em' }}>Summary</p>
        {rows.map(r => <Row key={r.label} label={r.label} value={r.value} />)}

        {summaryRows.length > 0 && <>
          <p style={{ margin: '16px 0 8px', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
            textTransform: 'uppercase', letterSpacing: '0.06em' }}>Scores / Timing</p>
          {summaryRows.map(r => <Row key={r.label} label={r.label} value={r.value} />)}
        </>}

        {candidates.length > 0 && <>
          <p style={{ margin: '16px 0 8px', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
            textTransform: 'uppercase', letterSpacing: '0.06em' }}>Face Candidates</p>
          <div style={{ background: 'var(--surface-alt)', borderRadius: 8, padding: '10px 12px' }}>
            {candidates.map(([name, score]) => {
              const w   = Math.min(Math.round(score * 100), 100);
              const col = score >= 0.45 ? '#22c55e' : score >= 0.35 ? '#f59e0b' : '#ef4444';
              return (
                <div key={name} style={{ marginBottom: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                    <span style={{ fontSize: 12, color: 'var(--text)' }}>{name}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: col }}>{Math.round(score * 100)}%</span>
                  </div>
                  <div style={{ height: 4, borderRadius: 2, background: 'var(--border)', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${w}%`, background: col, borderRadius: 2 }} />
                  </div>
                </div>
              );
            })}
            {t.face_score != null && (
              <p style={{ margin: '8px 0 0', fontSize: 10, color: 'var(--text-muted)' }}>
                Threshold: {Math.round((t.known_face_threshold ?? 0.45) * 100)}% — alert fired because no candidate exceeded it
              </p>
            )}
          </div>
        </>}

        {Object.keys(t).some(k => k.startsWith('ev_')) && <>
          <p style={{ margin: '16px 0 8px', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
            textTransform: 'uppercase', letterSpacing: '0.06em' }}>Detection Evidence</p>
          <EvTiles ev={t} />
        </>}

        {data.false_reason && <>
          <p style={{ margin: '16px 0 8px', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
            textTransform: 'uppercase', letterSpacing: '0.06em' }}>False Alert Reason</p>
          <Row label="Reason" value={data.false_reason} />
        </>}

        <p style={{ margin: '16px 0 8px', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
          textTransform: 'uppercase', letterSpacing: '0.06em' }}>Label This Alert</p>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          <button disabled={saving} onClick={() => { saveVerdict('correct'); setShowFalseOpts(false); }}
            style={{ flex: 1, padding: '8px 0', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13,
              background: verdict === 'correct' ? '#22c55e' : 'rgba(34,197,94,0.12)',
              color: verdict === 'correct' ? '#fff' : '#22c55e' }}>
            ✓ Correct Alert
          </button>
          <button disabled={saving} onClick={() => setShowFalseOpts(s => !s)}
            style={{ flex: 1, padding: '8px 0', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13,
              background: verdict === 'false_alert' ? '#ef4444' : 'rgba(239,68,68,0.12)',
              color: verdict === 'false_alert' ? '#fff' : '#ef4444' }}>
            ✗ False Alert
          </button>
          {allowsWrongId && <button disabled={saving} onClick={() => { saveVerdict('wrong_unknown'); setShowFalseOpts(false); }}
            style={{ flex: 1, padding: '8px 0', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13,
              background: verdict === 'wrong_unknown' ? '#f59e0b' : 'rgba(245,158,11,0.12)',
              color: verdict === 'wrong_unknown' ? '#fff' : '#f59e0b' }}>
            ⚠ Wrong ID
          </button>}
        </div>

        {showFalseOpts && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8, padding: '10px 12px',
            background: 'var(--surface-alt)', borderRadius: 8, border: '1px solid var(--border)' }}>
            <p style={{ width: '100%', margin: '0 0 6px', fontSize: 11, color: 'var(--text-muted)' }}>Select false alert reason:</p>
            {reasonOpts.map(r => (
              <button key={r.value} disabled={saving}
                onClick={() => { saveVerdict('false_alert', r.value); setShowFalseOpts(false); }}
                style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid var(--border)', cursor: 'pointer', fontSize: 12,
                  background: falseReason === r.value ? '#ef4444' : 'var(--surface)',
                  color: falseReason === r.value ? '#fff' : 'var(--text)', fontWeight: falseReason === r.value ? 600 : 400 }}>
                {r.label}
              </button>
            ))}
          </div>
        )}

        {saveMsg && <p style={{ margin: '4px 0 8px', fontSize: 12, color: saveMsg.ok ? '#22c55e' : '#ef4444' }}>{saveMsg.text}</p>}

        <button onClick={download}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            width: '100%', marginTop: 8, padding: '9px 0',
            background: 'var(--surface-alt)', border: '1px solid var(--border)',
            borderRadius: 8, color: 'var(--text)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          ⬇️ Download Full JSON
        </button>
      </div>
    </div>
  );
}

// ─── ActionModal — with existing-contact picker (mirrors Telegram flow) ────────
function ActionModal({ filename, mode, onClose, onDone }) {
  // step: 'loading' | 'pick' | 'new'
  const [step,      setStep]    = useState('loading');
  const [persons,   setPersons] = useState([]);
  const [name,      setName]    = useState('');
  const [profile,   setProfile] = useState('day');
  const [busy,      setBusy]    = useState(false);
  const [err,       setErr]     = useState('');
  const inputRef = useRef(null);

  const isKnown = mode === 'known';
  const title   = isKnown ? '👤 Mark as Known' : '🚫 Ban Person';

  // Load existing contacts on mount
  useEffect(() => {
    const endpoint = isKnown ? '/faces/known' : '/faces/banned';
    apiFetch(endpoint)
      .then(r => r.ok ? r.json() : { persons: [] })
      .then(d => {
        const list = (d.persons || []).map(p => p.name).sort();
        setPersons(list);
        setStep('pick');
      })
      .catch(() => setStep('pick')); // on error, go straight to pick (empty list)
  }, [isKnown]);

  // Auto-focus name input when switching to 'new'
  useEffect(() => {
    if (step === 'new') inputRef.current?.focus();
  }, [step]);

  const enrollExisting = async (existingName) => {
    setBusy(true); setErr('');
    try {
      let r;
      if (isKnown) {
        r = await apiFetch(`/faces/known/${encodeURIComponent(existingName)}/from_alert?filename=${encodeURIComponent(filename)}&profile=${profile}`, { method: 'POST' });
      } else {
        r = await apiFetch(`/faces/banned/${encodeURIComponent(existingName)}/from_alert?filename=${encodeURIComponent(filename)}`, { method: 'POST' });
      }
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        setErr(body.detail || 'Enrollment failed — check server');
        setBusy(false);
        return;
      }
      onDone(`Added to ${existingName}`);
    } catch {
      setErr('Request failed — check server');
      setBusy(false);
    }
  };

  const enrollNew = async (e) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) { setErr('Enter a name'); return; }
    setBusy(true); setErr('');
    try {
      let r;
      if (isKnown) {
        r = await apiFetch(`/faces/known/${encodeURIComponent(n)}/from_alert?filename=${encodeURIComponent(filename)}&profile=${profile}`, { method: 'POST' });
      } else {
        r = await apiFetch(`/faces/banned/${encodeURIComponent(n)}/from_alert?filename=${encodeURIComponent(filename)}`, { method: 'POST' });
      }
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        setErr(body.detail || 'Enrollment failed — check server');
        setBusy(false);
        return;
      }
      onDone(isKnown ? `${n} enrolled as known` : `${n} banned`);
    } catch {
      setErr('Request failed — check server');
      setBusy(false);
    }
  };

  return (
    <div className="pk-lightbox" onClick={onClose}
      style={{ padding: '24px 16px', overflowY: 'auto', alignItems: 'center', justifyContent: 'flex-start' }}>
      <div
        style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14,
                 padding: 24, width: '100%', maxWidth: 380,
                 maxHeight: 'calc(100vh - 120px)', overflowY: 'auto', flexShrink: 0 }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <h3 style={{ margin: 0, fontSize: 15, color: 'var(--text)' }}>
            {step === 'new' ? (isKnown ? '👤 New Person' : '🚫 New Entry') : title}
          </h3>
          <button className="pk-lightbox-close" style={{ position: 'static' }} onClick={onClose}>✕</button>
        </div>

        {/* Loading */}
        {step === 'loading' && (
          <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-muted)', fontSize: 13 }}>
            ⏳ Loading contacts…
          </div>
        )}

        {/* Pick step — list existing + New Person button */}
        {step === 'pick' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {persons.length > 0 && (
              <>
                <p style={{ margin: '0 0 4px', fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>
                  Add to existing
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto' }}>
                  {persons.map(pName => (
                    <button
                      key={pName}
                      disabled={busy}
                      onClick={() => enrollExisting(pName)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        background: 'var(--surface2, rgba(255,255,255,0.04))',
                        border: '1px solid var(--border)', borderRadius: 8,
                        padding: '9px 14px', cursor: 'pointer', color: 'var(--text)',
                        fontSize: 14, textAlign: 'left', transition: 'background 0.15s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--hover, rgba(255,255,255,0.08))'}
                      onMouseLeave={e => e.currentTarget.style.background = 'var(--surface2, rgba(255,255,255,0.04))'}
                    >
                      <span style={{
                        width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                        background: isKnown ? '#2ecc71' : '#e74c3c',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 700, color: '#fff',
                      }}>
                        {pName.charAt(0).toUpperCase()}
                      </span>
                      {pName}
                    </button>
                  ))}
                </div>
                <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />
              </>
            )}

            {/* Day/Night profile selector — shown in pick step for known mode */}
            {isKnown && persons.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 2 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Profile</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {['day', 'night'].map(p => (
                    <button key={p} type="button"
                      className={`pk-filter-tab${profile === p ? ' active' : ''}`}
                      onClick={() => setProfile(p)}>
                      {p === 'day' ? '☀️ Day' : '🌙 Night'}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={() => setStep('new')}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                background: 'transparent',
                border: `1px dashed ${isKnown ? '#2ecc71' : '#e74c3c'}`,
                borderRadius: 8, padding: '9px 14px', cursor: 'pointer',
                color: isKnown ? '#2ecc71' : '#e74c3c',
                fontSize: 14, fontWeight: 600, transition: 'background 0.15s',
              }}
            >
              ➕ New Person
            </button>

            {err && <div style={{ fontSize: 12, color: '#e74c3c' }}>{err}</div>}
            {busy && <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>⏳ Enrolling…</div>}
          </div>
        )}

        {/* New person step */}
        {step === 'new' && (
          <form onSubmit={enrollNew} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <button
              type="button"
              onClick={() => { setStep('pick'); setErr(''); setName(''); }}
              style={{
                alignSelf: 'flex-start', background: 'transparent', border: 'none',
                color: 'var(--text-muted)', cursor: 'pointer', fontSize: 13, padding: 0,
              }}
            >
              ← Back
            </button>
            <input
              ref={inputRef}
              className="alert-inline-input"
              style={{ padding: '9px 12px', fontSize: 14 }}
              placeholder="Person name…"
              value={name}
              onChange={e => setName(e.target.value)}
            />
            {isKnown && (
              <div style={{ display: 'flex', gap: 8 }}>
                {['day', 'night'].map(p => (
                  <button key={p} type="button"
                    className={`pk-filter-tab${profile === p ? ' active' : ''}`}
                    onClick={() => setProfile(p)}>
                    {p === 'day' ? '☀️ Day' : '🌙 Night'}
                  </button>
                ))}
              </div>
            )}
            {err && <div style={{ fontSize: 12, color: '#e74c3c' }}>{err}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="pk-btn" onClick={onClose}>Cancel</button>
              <button type="submit" className={`pk-btn ${isKnown ? 'success' : 'danger'}`} disabled={busy}>
                {busy ? '⏳…' : isKnown ? '✓ Enroll' : '🚫 Ban'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

const fmtDate = (d) => {
  const y  = d.getFullYear();
  const m  = String(d.getMonth() + 1).padStart(2, '0');
  const dy = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dy}`;
};

// Build the &time_from/&time_to query fragment from HH:MM inputs.
// Empty inputs are omitted so the backend falls back to the whole day.
const buildTimeParam = (tf, tt) => {
  const f = (tf || '').replace(/:/g, '');
  const t = (tt || '').replace(/:/g, '');
  return (f ? `&time_from=${f}` : '') + (t ? `&time_to=${t}` : '');
};

function AlertCamPicker({ cameras, value, onChange }) {
  const [open,   setOpen]   = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const menuRef    = useRef(null);

  const openMenu = () => {
    if (triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setCoords({ top: r.bottom + 6, left: r.left, width: Math.max(r.width, 180) });
    }
    setOpen(true);
  };

  // Menu width depends on camera-name length, so it can't be clamped up
  // front like the fixed-width BulkMarkMenu — measure it once mounted and
  // pull it back onscreen if it would run past the right edge (toolbar
  // trigger sits near the end of a wide desktop window).
  useLayoutEffect(() => {
    if (!open || !menuRef.current) return;
    const menuW = menuRef.current.offsetWidth;
    const maxLeft = window.innerWidth - menuW - 8;
    if (coords.left > maxLeft) {
      setCoords(c => ({ ...c, left: Math.max(8, maxLeft) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const h = e => {
      if (triggerRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const options = [
    { label: 'All Cameras', value: null },
    ...cameras.map(c => ({ label: c.name, value: c.slug, sub: `Cam ${c.streamIdx + 1}` })),
  ];
  const selected = options.find(o => o.value === value) || options[0];

  return (
    <>
      <button
        ref={triggerRef}
        className={`alrt-cam-trigger${open ? ' open' : ''}${value !== null ? ' filtered' : ''}`}
        onClick={() => open ? setOpen(false) : openMenu()}
      >
        <span>📷</span>
        <span className="alrt-cam-label">{selected.label}</span>
        {selected.sub && <span className="alrt-cam-sub">{selected.sub}</span>}
        <svg width="9" height="9" viewBox="0 0 9 9" style={{ flexShrink:0, transition:'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none' }}>
          <path d="M1.5 3l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
        </svg>
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          className="alrt-cam-menu"
          style={{ position:'fixed', top: coords.top, left: coords.left, minWidth: coords.width, zIndex: 9999, pointerEvents: 'auto' }}
        >
          {options.map(o => (
            <button key={String(o.value)} className={`alrt-cam-item${o.value === value ? ' active' : ''}`}
              onClick={() => { onChange(o.value); setOpen(false); }}>
              <span>{o.value === null ? '📷' : '🎥'}</span>
              <span className="alrt-cam-item-label">{o.label}</span>
              {o.sub && <span className="alrt-cam-item-sub">{o.sub}</span>}
              {o.value === value && <span className="alrt-cam-check">✓</span>}
            </button>
          ))}
        </div>,
        document.getElementById('lightbox-root')
      )}
    </>
  );
}

// Material-style picker — replaces native <select>. `compact` renders a small
// toolbar pill (for always-visible filters like Camera) instead of the full card.
function MobilePickerField({ label, value, options, onChange, compact = false }) {
  const [open, setOpen] = useState(false);
  const selected = options.find(o => o.value === value) || options[0];
  return (
    <>
      {compact ? (
        <button className={`tb-btn${value !== options[0].value ? ' active' : ''}`} onClick={() => setOpen(true)}>
          {selected?.label} ⌄
        </button>
      ) : (
        <button className="mob-field" onClick={() => setOpen(true)}>
          <span className="mob-field-text">
            <span className="mob-field-label">{label}</span>
            <span className="mob-field-value">{selected?.label}</span>
          </span>
          <span className="mob-field-chevron">⌄</span>
        </button>
      )}
      {open && (
        <div className="dash-sheet-overlay" style={{ zIndex: 9600 }} onClick={() => setOpen(false)}>
          <div className="dash-sheet" onClick={e => e.stopPropagation()}>
            <div className="dash-sheet-handle" />
            <h3>{label}</h3>
            <div className="mob-option-list">
              {options.map(o => (
                <button key={String(o.value)} className={`mob-option-row${o.value === value ? ' active' : ''}`}
                  onClick={() => { onChange(o.value); setOpen(false); }}>
                  <span>{o.label}</span>
                  {o.value === value && <span className="mob-option-check">✓</span>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Bulk-mark false-alert reasons (codes match backend FA_REASONS / analysis).
const BULK_FA_REASONS = [
  { code: 'sta',  label: '🗿 Static Object' },
  { code: 'fol',  label: '🌿 Foliage' },
  { code: 'aml',  label: '🐾 Animal' },
  { code: 'car',  label: '🚗 Headlights' },
  { code: 'rain', label: '🌧 Rain / Fog' },
  { code: 'ins',  label: '🦟 Insect' },
  { code: 'fan',  label: '💨 Fan / Rotating' },
  { code: 'shd',  label: '🌑 Shadow' },
  { code: 'oth',  label: '❓ Other' },
];

// Bulk verdict dropdown — shown when rows are selected. Filter → select-all →
// Mark. Posts the selected ids to /alerts/bulk-verdict via onMark. Uses a
// fixed-position menu (computed from the button rect) so it isn't clipped by
// the toolbar's overflow.
function BulkMarkMenu({ count, onMark, reasons = BULK_FA_REASONS, note = null }) {
  const [open,   setOpen]   = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const btnRef  = useRef(null);
  const menuRef = useRef(null);
  const MENU_W  = 210;

  const openMenu = () => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setCoords({
        top:  r.bottom + 6,
        left: Math.max(8, Math.min(r.left, window.innerWidth - MENU_W - 8)),
      });
    }
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const h = e => {
      if (btnRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const itemStyle = {
    display: 'block', width: '100%', textAlign: 'left', background: 'transparent',
    border: 'none', cursor: 'pointer', padding: '8px 12px', borderRadius: 6,
    fontSize: 13, color: 'var(--text)',
  };
  return (
    <>
      <button ref={btnRef} className="tb-btn" style={{ color: '#22c55e', borderColor: '#22c55e' }}
        onClick={() => open ? setOpen(false) : openMenu()}>
        🏷 Mark ({count}) ⌄
      </button>
      {open && createPortal(
        <div ref={menuRef} style={{ position: 'fixed', top: coords.top, left: coords.left,
          width: MENU_W, zIndex: 9999, pointerEvents: 'auto',
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10,
          boxShadow: '0 8px 24px rgba(0,0,0,0.25)', padding: 4 }}>
          <button style={{ ...itemStyle, color: '#22c55e', fontWeight: 700 }}
            onClick={() => { onMark('correct', null); setOpen(false); }}>
            ✓ Correct
          </button>
          <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />
          <div style={{ fontSize: 10, color: 'var(--text-muted)', padding: '2px 12px',
            textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            🔴 False alert — reason
          </div>
          {note && <div style={{ fontSize: 11, color: 'var(--text-muted)', padding: '4px 12px' }}>{note}</div>}
          {reasons.map(r => (
            <button key={r.code} style={itemStyle}
              onClick={() => { onMark('false_alert', r.code); setOpen(false); }}>
              {r.label}
            </button>
          ))}
        </div>,
        document.getElementById('lightbox-root')
      )}
    </>
  );
}

export default function AlertsPage() {
  useEffect(() => { localStorage.setItem('lastAlertVisit', Date.now()); }, []);

  const [alerts,    setAlerts]    = useState([]);
  const [total,     setTotal]     = useState(0);
  const [hasMore,   setHasMore]   = useState(false);
  const [loading,     setLoading]     = useState(true);
  const [fetching,    setFetching]    = useState(false);
  const [refreshing,  setRefreshing]  = useState(false);
  const [filter,         setFilter]         = useState('all');
  const [camFilter,      setCamFilter]      = useState(null);
  const [verdictFilter,  setVerdictFilter]  = useState('all');
  const [bellFilter,     setBellFilter]     = useState(false);   // only alerts that rang the doorbell
  const [cameras,        setCameras]        = useState([]);
  const [mediaMode,      setMediaMode]      = useState('image');
  const [showFilters,    setShowFilters]    = useState(false);
  const [idQuery,        setIdQuery]        = useState('');
  const [dateFrom,  setDateFrom]  = useState(() => fmtDate(new Date(Date.now() - 86400000)));
  const [dateTo,    setDateTo]    = useState(() => fmtDate(new Date()));
  const [timeFrom,  setTimeFrom]  = useState('');   // HH:MM time-of-day, '' = start of day
  const [timeTo,    setTimeTo]    = useState('');   // HH:MM time-of-day, '' = end of day
  const [lightbox,     setLightbox]     = useState(null); // { path, meta, idx }
  // Full View labelling. Index into `visible`, so it walks exactly the filtered set.
  const [fullViewIdx,  setFullViewIdx]  = useState(null);
  const [lbSrc,        setLbSrc]        = useState(null);
  const [toast,        setToast]        = useState(null);
  const [actionModal,  setActionModal]  = useState(null); // { filename, mode: 'known'|'ban' }
  const [faceModal,    setFaceModal]    = useState(null); // { alertId, filename }
  const [camOffModal,  setCamOffModal]  = useState(null); // is_camera_offline alert row
  const [selected,     setSelected]     = useState(new Set()); // filenames
  const [confirm,      setConfirm]      = useState(null); // { title, message, onConfirm }
  const [alertBanner,  setAlertBanner]  = useState(null); // null | { added: number }
  const [logModal,     setLogModal]     = useState(null); // { data, filename }
  const [dlBusy,       setDlBusy]       = useState(false);
  const [mobileSheetOpen,  setMobileSheetOpen]  = useState(false);
  const [mobileMenuOpen,   setMobileMenuOpen]   = useState(false);
  const [videoLightbox,    setVideoLightbox]    = useState(null); // { vidPath, filename }
  const [downloadingClip,  setDownloadingClip]  = useState(false);
  const [cardMenuOpen,     setCardMenuOpen]     = useState(null); // filename of card whose ⋮ menu is open
  const isMobile = useIsMobile();

  const sentinelRef        = useRef(null);
  const fetchingRef        = useRef(false);
  const pageRef            = useRef(0);
  const hasMoreRef         = useRef(false);
  const dateFromRef        = useRef(dateFrom);
  const dateToRef          = useRef(dateTo);
  const timeFromRef        = useRef(timeFrom);
  const timeToRef          = useRef(timeTo);
  const camFilterRef       = useRef(camFilter);
  const verdictFilterRef   = useRef(verdictFilter);
  const bellFilterRef      = useRef(bellFilter);
  const latestIdRef        = useRef(null);
  const latestTotRef       = useRef(0);
  const lastScrollRef      = useRef(Date.now());
  const idleReloadTimerRef = useRef(null);
  // PRVSK-84 unread handling. The first list load waits for the unread check so
  // the date range can be widened to cover every unread alert first.
  const [unreadReady, setUnreadReady] = useState(false);
  const prevSeenRef    = useRef(null);   // last_seen before this visit → "New" tags
  const seenPendingRef = useRef(true);   // mark seen after the next unfiltered load

  const showToast = (msg, type = 'ok') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2800);
  };

  const handleDownloadClip = async () => {
    if (!videoLightbox || downloadingClip) return;
    setDownloadingClip(true);
    try {
      await fetchAndSaveFile(videoLightbox.vidPath, videoLightbox.filename);
      showToast('Video downloaded');
    } catch {
      showToast('Download failed', 'err');
    } finally {
      setDownloadingClip(false);
    }
  };

  // Card tiles show either the alert image or its video clip (mediaMode
  // toggle) — download/share act on whichever is currently displayed.
  const cardMediaTarget = (alert, alertId, imgPath, vidPath) => (
    mediaMode === 'video'
      ? { path: vidPath, filename: `${alertId}.mp4`, mime: 'video/mp4' }
      : { path: imgPath, filename: alert.filename,   mime: 'image/jpeg' }
  );

  const handleDownloadMedia = async (alert, alertId, imgPath, vidPath) => {
    const { path, filename } = cardMediaTarget(alert, alertId, imgPath, vidPath);
    try {
      await fetchAndSaveFile(path, filename);
      showToast(`${mediaMode === 'video' ? 'Video' : 'Image'} downloaded`);
    } catch {
      showToast('Download failed', 'err');
    }
  };

  const handleShareMedia = async (alert, alertId, imgPath, vidPath) => {
    const { path, filename, mime } = cardMediaTarget(alert, alertId, imgPath, vidPath);
    try {
      const result = await shareOrDownloadFile(path, filename, mime);
      if (result === 'downloaded') showToast('Sharing not supported here — downloaded instead');
    } catch {
      showToast('Share failed', 'err');
    }
  };

  // Close the per-card ⋮ menu on any click outside it (button/menu items
  // stopPropagation so this only fires for genuine outside clicks).
  useEffect(() => {
    if (!cardMenuOpen) return;
    const close = () => setCardMenuOpen(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [cardMenuOpen]);

  const loadPage = useCallback(async (p, reset = false, silent = false) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    if (reset && !silent) setLoading(true);
    else if (silent)      setRefreshing(true);
    else                  setFetching(true);
    const silentStart = silent ? Date.now() : 0;
    try {
      const from          = dateFromRef.current.replace(/-/g, '');
      const to            = dateToRef.current.replace(/-/g, '');
      const timeParam     = buildTimeParam(timeFromRef.current, timeToRef.current);
      const camParam      = camFilterRef.current !== null ? `&cam=${camFilterRef.current}` : '';
      const verdictParam  = verdictFilterRef.current !== 'all' ? `&verdict=${verdictFilterRef.current}` : '';
      const r = await apiFetch(`/ui/alerts?page=${p}&per_page=${PER_PAGE}&exclude=snapshot&date_from=${from}&date_to=${to}${timeParam}${camParam}${verdictParam}${bellFilterRef.current ? '&doorbell=true' : ''}`);
      const d = await r.json();
      const items = d.alerts || [];
      // Mark this device's alerts seen up to the newest alert actually shown,
      // so one arriving mid-load stays unread. Only for the unfiltered list:
      // a camera/verdict filter must not mark other cameras' alerts as seen.
      if (reset && p === 0 && seenPendingRef.current
          && camFilterRef.current === null && verdictFilterRef.current === 'all' && !bellFilterRef.current) {
        seenPendingRef.current = false;
        const newest = items.map(a => stampFromFilename(a.filename)).find(Boolean) || '';
        markAlertsSeen(newest);
      }
      setAlerts(prev => reset ? items : [...prev, ...items]);
      setTotal(d.total || 0);
      pageRef.current      = p;
      latestIdRef.current  = items[0]?.filename ?? null;
      latestTotRef.current = d.total || 0;
      const more = items.length === PER_PAGE;
      hasMoreRef.current = more;
      setHasMore(more);
      cacheAlertsToDevice(items);
    } catch {
      showToast('Failed to load alerts', 'err');
    } finally {
      fetchingRef.current = false;
      setLoading(false);
      setFetching(false);
      if (silent) {
        const elapsed = Date.now() - silentStart;
        const wait = Math.max(0, 1000 - elapsed);
        if (wait > 0) setTimeout(() => setRefreshing(false), wait);
        else setRefreshing(false);
      } else {
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    apiFetch('/cameras')
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        const list = (data.cameras || []).map(cam => {
          const idx = cam.idx;
          const name = cam.name ? cam.name.replace(/\b\w/g, c => c.toUpperCase()) : `Cam ${idx + 1}`;
          const slug = cam.name
            ? cam.name.replace(/[^A-Za-z0-9]+/g, '_').replace(/^_|_$/g, '') || `cam${idx}`
            : `cam${idx}`;
          return { streamIdx: idx, name, slug };
        });
        setCameras(list);
      })
      .catch(() => {});
  }, []);

  // Unread check before the first load: if there are unread alerts older than
  // the default window (yesterday → today), start the range at the oldest
  // unread alert's date so every counted alert is on screen. The range only
  // ever moves back; recent unread alerts keep the default window.
  useEffect(() => {
    let cancelled = false;
    fetchUnread().then(d => {
      if (cancelled) return;
      if (d) {
        prevSeenRef.current = d.last_seen || null;
        if (d.count > 0 && /^\d{8}$/.test(d.oldest_date || '')) {
          const od = `${d.oldest_date.slice(0, 4)}-${d.oldest_date.slice(4, 6)}-${d.oldest_date.slice(6, 8)}`;
          setDateFrom(cur => (od < cur ? od : cur));
          setCamFilter(null);
          setVerdictFilter('all');
        }
      }
      setUnreadReady(true);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!unreadReady) return;
    dateFromRef.current        = dateFrom;
    dateToRef.current          = dateTo;
    timeFromRef.current        = timeFrom;
    timeToRef.current          = timeTo;
    camFilterRef.current       = camFilter;
    verdictFilterRef.current   = verdictFilter;
    bellFilterRef.current      = bellFilter;
    pageRef.current            = 0;
    hasMoreRef.current         = false;
    loadPage(0, true);
  }, [unreadReady, dateFrom, dateTo, timeFrom, timeTo, camFilter, verdictFilter, bellFilter, loadPage]);

  useEffect(() => {
    window.__onAppForeground = () => {
      // Back in the foreground on this page: alerts that arrived meanwhile are
      // now on screen, so mark them seen again after the reload.
      seenPendingRef.current = true;
      loadPage(0, true, true);
    };
    return () => { window.__onAppForeground = null; };
  }, [loadPage]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore || fetching || loading) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && hasMoreRef.current && !fetchingRef.current) {
        loadPage(pageRef.current + 1, false);
      }
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, fetching, loading, loadPage]);

  // Track page scroll so idle-reload knows when user is active
  useEffect(() => {
    const onScroll = () => { lastScrollRef.current = Date.now(); };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Silent background poll — shows banner, auto-reloads after idle timeout
  useEffect(() => {
    const ms = getAlertPollMs();
    if (!ms) return;

    const scheduleIdleReload = () => {
      if (idleReloadTimerRef.current) clearTimeout(idleReloadTimerRef.current);
      const idleMs = getIdleReloadMs();
      if (!idleMs) return;
      const check = () => {
        const idleSince = Date.now() - lastScrollRef.current;
        if (idleSince >= idleMs) {
          setAlertBanner(null);
          idleReloadTimerRef.current = null;
          lastScrollRef.current = Date.now();
          loadPage(0, true);
        } else {
          idleReloadTimerRef.current = setTimeout(check, idleMs - idleSince);
        }
      };
      idleReloadTimerRef.current = setTimeout(check, idleMs);
    };

    const silentCheck = async () => {
      if (document.visibilityState !== 'visible') return;
      if (fetchingRef.current) return;
      try {
        const from         = dateFromRef.current.replace(/-/g, '');
        const to           = dateToRef.current.replace(/-/g, '');
        const timeParam    = buildTimeParam(timeFromRef.current, timeToRef.current);
        const camParam     = camFilterRef.current !== null ? `&cam=${camFilterRef.current}` : '';
        const verdictParam = verdictFilterRef.current !== 'all' ? `&verdict=${verdictFilterRef.current}` : '';
        const r = await apiFetch(`/ui/alerts?page=0&per_page=1&exclude=snapshot&date_from=${from}&date_to=${to}${timeParam}${camParam}${verdictParam}${bellFilterRef.current ? '&doorbell=true' : ''}`);
        const d = await r.json();
        const newTotal  = d.total || 0;
        const newLatest = (d.alerts || [])[0]?.filename ?? null;
        if (newTotal !== latestTotRef.current || newLatest !== latestIdRef.current) {
          const added = Math.max(0, newTotal - latestTotRef.current);
          latestIdRef.current  = newLatest;
          latestTotRef.current = newTotal;
          const idleMs = getIdleReloadMs();
          const idleSince = Date.now() - lastScrollRef.current;
          if (idleMs && idleSince >= idleMs) {
            // User idle — full reload
            setAlertBanner(null);
            lastScrollRef.current = Date.now();
            loadPage(0, true);
          } else if (added > 0 && added <= 10) {
            // Fetch and prepend new alerts — scroll position stays put
            try {
              const nr = await apiFetch(`/ui/alerts?page=0&per_page=${added}&exclude=snapshot&date_from=${from}&date_to=${to}${timeParam}${camParam}${verdictParam}${bellFilterRef.current ? '&doorbell=true' : ''}`);
              const nd = await nr.json();
              const newItems = nd.alerts || [];
              if (newItems.length > 0) {
                setAlerts(prev => {
                  const existingIds = new Set(prev.map(a => a.filename));
                  const fresh = newItems.filter(a => !existingIds.has(a.filename));
                  return fresh.length > 0 ? [...fresh, ...prev] : prev;
                });
                setTotal(newTotal);
                setAlertBanner({ added, prepended: true });
                setTimeout(() => setAlertBanner(null), 4000);
              }
            } catch {
              setAlertBanner(prev => ({ added: (prev?.added || 0) + added }));
              scheduleIdleReload();
            }
          } else if (added > 10) {
            // Too many new alerts — show banner, require manual refresh
            setAlertBanner(prev => ({ added: (prev?.added || 0) + added }));
            scheduleIdleReload();
          }
          // added === 0: latest ID changed but count same (e.g. delete raced with add) — ignore
        }
      } catch { /* silent */ }
    };

    let id = null;
    const start = () => { if (!id) id = setInterval(silentCheck, ms); };
    const stop  = () => { clearInterval(id); id = null; };
    const onVis = () => {
      if (document.visibilityState === 'visible') { start(); loadPage(0, true, true); }
      else stop();
    };

    document.addEventListener('visibilitychange', onVis);
    start();
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVis);
      if (idleReloadTimerRef.current) clearTimeout(idleReloadTimerRef.current);
    };
  }, [loadPage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!lightbox) { setLbSrc(null); return; }
    let url;
    apiFetch(lightbox.path)
      .then(r => r.ok ? r.blob() : null)
      .then(blob => { if (blob) { url = URL.createObjectURL(blob); setLbSrc(url); } })
      .catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [lightbox]);

  const deleteAll = () => setConfirm({
    title: 'Delete all alerts?',
    message: `This will permanently remove all ${total} alerts and cannot be undone.`,
    onConfirm: async () => {
      setConfirm(null);
      try {
        await apiFetch('/alerts', { method: 'DELETE' });
        setAlerts([]); setTotal(0); setHasMore(false); setSelected(new Set());
        showToast('All alerts deleted');
      } catch { showToast('Delete failed', 'err'); }
    },
  });

  const openAlertLog = async (filename) => {
    try {
      const res  = await apiFetch(`/alerts/${encodeURIComponent(filename)}/info`);
      const data = await res.json();
      setLogModal({ data, filename });
      // Opportunistically enrich the offline cache with this alert's evidence.
      enrichOfflineDetail(filename, data);
    } catch { showToast('No log info for this alert', 'err'); }
  };

  const downloadLogs = async (filenames) => {
    if (!filenames.length || dlBusy) return;
    setDlBusy(true);
    showToast(`Fetching ${filenames.length} log${filenames.length > 1 ? 's' : ''}…`);
    try {
      const n = await downloadAlertLogs(filenames);
      showToast(n ? `Downloaded ${n} log${n > 1 ? 's' : ''}` : 'No log records found', n ? undefined : 'err');
    } catch { showToast('Download failed', 'err'); }
    finally { setDlBusy(false); }
  };

  // The snapshots for the same selection, zipped server-side. Telemetry without the
  // picture is half a training set, and the cap lives on the server because that is
  // where the files are — the check here only saves a pointless round trip.
  const downloadImages = async (filenames) => {
    if (!filenames.length || dlBusy) return;
    if (filenames.length > IMG_EXPORT_MAX) {
      showToast(`Capped at ${IMG_EXPORT_MAX} images — narrow the filter`, 'err');
      return;
    }
    setDlBusy(true);
    showToast(`Zipping ${filenames.length} image${filenames.length > 1 ? 's' : ''}…`);
    try {
      const r = await apiFetch('/alerts/export-images', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: filenames }),
      });
      if (!r.ok) {
        let msg = `Image download failed (${r.status})`;
        try { const j = await r.json(); if (j?.detail) msg = j.detail; } catch { /* keep default */ }
        showToast(msg, 'err');
        return;
      }
      downloadFile2(`alert_images_${filenames.length}.zip`, await r.blob());
      showToast(`Downloaded ${filenames.length} image${filenames.length > 1 ? 's' : ''}`);
    } catch { showToast('Image download failed', 'err'); }
    finally { setDlBusy(false); }
  };

  const deleteOne = (filename) => setConfirm({
    title: 'Delete this alert?',
    message: 'This alert will be permanently removed.',
    onConfirm: async () => {
      setConfirm(null);
      try {
        await apiFetch(`/alerts/${encodeURIComponent(filename)}`, { method: 'DELETE' });
        const next = alerts.filter(a => a.filename !== filename);
        setAlerts(next);
        setTotal(prev => Math.max(0, prev - 1));
        latestTotRef.current = Math.max(0, latestTotRef.current - 1);
        latestIdRef.current  = next[0]?.filename ?? null;
        setSelected(prev => { const s = new Set(prev); s.delete(filename); return s; });
        showToast('Alert deleted');
        if (next.length < 5) loadPage(0, true);
      } catch { showToast('Delete failed', 'err'); }
    },
  });

  const deleteSelected = () => {
    if (!selected.size) return;
    const names = [...selected];
    setConfirm({
      title: `Delete ${names.length} alert${names.length > 1 ? 's' : ''}?`,
      message: 'Selected alerts will be permanently removed.',
      onConfirm: async () => {
        setConfirm(null);
        await Promise.all(names.map(fn =>
          apiFetch(`/alerts/${encodeURIComponent(fn)}`, { method: 'DELETE' }).catch(() => {})
        ));
        const next = alerts.filter(a => !names.includes(a.filename));
        setAlerts(next);
        setTotal(prev => Math.max(0, prev - names.length));
        latestTotRef.current = Math.max(0, latestTotRef.current - names.length);
        latestIdRef.current  = next[0]?.filename ?? null;
        setSelected(new Set());
        showToast(`${names.length} alert${names.length > 1 ? 's' : ''} deleted`);
        if (next.length < 5) loadPage(0, true);
      },
    });
  };

  // Face-cover alerts have their own reasons, and the box refuses a selection that
  // mixes them with other alerts — so the menu offers what the selection can take.
  const bulkReasonProps = useMemo(() => {
    const ids = [...selected];
    const fc = ids.filter(isFaceCoverAlert).length;
    if (!fc) return {};
    if (fc === ids.length) return { reasons: FC_REASONS.map(r => ({ ...r, code: r.value })) };
    return { reasons: [], note: 'Face-cover alerts are marked separately — select only those to give a reason.' };
  }, [selected]);

  const markSelected = async (verdict, reason = null) => {
    if (!selected.size) return;
    const ids = [...selected];
    try {
      const r = await apiFetch('/alerts/bulk-verdict', {
        method: 'POST',
        json: { ids, verdict, false_reason: reason },
      });
      if (!r.ok) throw new Error(await r.text());
      const res = await r.json();
      setAlerts(prev => prev.map(a =>
        ids.includes(a.filename) ? { ...a, verdict, false_reason: reason } : a));
      setSelected(new Set());
      showToast(`Marked ${res.updated} alert${res.updated !== 1 ? 's' : ''} as ${verdict === 'correct' ? 'correct' : 'false alert'}`);
    } catch {
      showToast('Bulk mark failed', 'err');
    }
  };

  // Single-alert verdict save for Full View. The card component owns its own copy of
  // this, but Full View sits above the cards and needs the list state updated so the
  // "N unlabelled" counter and auto-advance stay honest.
  const saveVerdictFullView = async (filename, verdict, reason = null) => {
    try {
      const res = await apiFetch(`/alerts/${filename}/verdict`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ verdict, false_reason: reason }),
      });
      if (!res.ok) throw new Error();
      setAlerts(prev => prev.map(a =>
        a.filename === filename ? { ...a, verdict, false_reason: reason } : a));
    } catch {
      showToast('Save failed', 'err');
    }
  };

  const toggleSelect = (filename) => {
    setSelected(prev => {
      const s = new Set(prev);
      s.has(filename) ? s.delete(filename) : s.add(filename);
      return s;
    });
  };

  const selectAll = () => setSelected(new Set(fileVisible.map(v => v.alert.filename)));
  const clearSelection = () => setSelected(new Set());

  const isFiltered = filter !== 'all' || camFilter !== null || verdictFilter !== 'all' || bellFilter
    || dateFrom !== fmtDate(new Date(Date.now() - 86400000)) || dateTo !== fmtDate(new Date())
    || timeFrom !== '' || timeTo !== '';
  // Camera is its own always-visible chip on mobile, so the "🔍 More Filters" dot
  // should only reflect what's actually hidden inside that sheet (Date/Time/Type/Verdict).
  const isMoreFiltered = filter !== 'all' || verdictFilter !== 'all' || bellFilter
    || dateFrom !== fmtDate(new Date(Date.now() - 86400000)) || dateTo !== fmtDate(new Date())
    || timeFrom !== '' || timeTo !== '';

  const resetFilters = () => {
    setFilter('all');
    setCamFilter(null);
    setVerdictFilter('all');
    setBellFilter(false);
    setDateFrom(fmtDate(new Date(Date.now() - 86400000)));
    setDateTo(fmtDate(new Date()));
    setTimeFrom('');
    setTimeTo('');
    setIdQuery('');
    setMediaMode('image');
  };

  // Jump to a pasted alert id. Ids are <type>_<Camera_Name>_YYYYMMDD_HHMMSS, so
  // the date and time can be read straight out of one and pushed into the filters
  // the list already sends to the server — no id lookup endpoint needed. Every
  // telemetry export and missed-alert record leads with the id, which is where
  // these get copied from.
  const jumpToId = (raw) => {
    const q = String(raw || '').trim().replace(/\.(jpg|jpeg|mp4|json)$/i, '');
    setIdQuery(q);
    const m = q.match(/_(\d{8})_(\d{2})(\d{2})(\d{2})$/);
    if (!m) return;                       // not an id — leave it as a plain text filter
    const [, ymd, hh, mm] = m;
    const mins = Number(hh) * 60 + Number(mm);
    const clock = v => `${String(Math.floor(v / 60)).padStart(2, '0')}:${String(v % 60).padStart(2, '0')}`;
    const day = `${ymd.slice(0, 4)}-${ymd.slice(4, 6)}-${ymd.slice(6, 8)}`;
    // +/- 2 min: the id stamps session start, the record can land a little later.
    setDateFrom(day);
    setDateTo(day);
    setTimeFrom(clock(Math.max(0, mins - 2)));
    setTimeTo(clock(Math.min(1439, mins + 2)));
    setCamFilter(null);
    setVerdictFilter('all');
    setFilter('all');
  };

  // A notification tap opens /alerts?alert=<id> (PRVSK-139): jump to it once.
  const deepLinked = useRef(false);
  const navigate = useNavigate();
  useEffect(() => {
    if (deepLinked.current) return;
    deepLinked.current = true;
    const params = new URLSearchParams(window.location.search);
    const id = params.get('alert');
    if (!id) return;
    jumpToId(id);
    // Drop ?alert= from the address once it's applied. Left there, Back, a reload
    // or the phone app reloading on resume re-applied the one-alert filter after
    // the user had left the page, and they had to clear it by hand.
    params.delete('alert');
    const qs = params.toString();
    navigate(`${window.location.pathname}${qs ? `?${qs}` : ''}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visible = alerts
    .map((a, i) => ({ alert: a, originalIdx: i }))
    .filter(({ alert: a }) => {
      if (filter !== 'all' && parseName(a.filename).type !== filter) return false;
      if (idQuery && !String(a.filename || '').toLowerCase().includes(idQuery.toLowerCase())) return false;
      return true;
    });
  // Real alert files only — the camera-offline row has no image/clip/log, so it's
  // left out of select-all and the bulk download actions.
  const fileVisible = visible.filter(v => !v.alert.is_camera_offline);
  const allTypes = ['all', 'unknown', 'banned', 'instant', 'motion'];

  const navigateLightbox = (dir) => {
    if (!lightbox) return;
    let nextIdx = lightbox.idx + dir;
    while (visible[nextIdx]?.alert.is_camera_offline) nextIdx += dir;   // no image to show
    if (nextIdx < 0 || nextIdx >= visible.length) return;
    const { alert } = visible[nextIdx];
    const meta = parseName(alert.filename);
    const imgPath = `/alerts/${encodeURIComponent(alert.filename)}/image`;
    setLightbox({ path: imgPath, meta, idx: nextIdx });
  };

  // Keyboard navigation for lightbox — must be after visible and navigateLightbox
  useEffect(() => {
    if (!lightbox) return;
    const handler = (e) => {
      if (e.key === 'ArrowRight') navigateLightbox(1);
      if (e.key === 'ArrowLeft')  navigateLightbox(-1);
      if (e.key === 'Escape') { setLightbox(null); setVideoLightbox(null); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightbox, videoLightbox, visible]); // eslint-disable-line

  // Tell Flutter to rotate to landscape when video opens, portrait when closed.
  // Cleanup on unmount ensures orientation resets if user navigates away mid-video.
  useEffect(() => {
    if (window.FlutterOrientation) {
      window.FlutterOrientation.postMessage(videoLightbox ? 'video:open' : 'video:close');
    }
  }, [videoLightbox]);

  useEffect(() => {
    return () => { if (window.FlutterOrientation) window.FlutterOrientation.postMessage('video:close'); };
  }, []);

  return (
    <div className="pk-page">
      {fullViewIdx !== null && (
        <ReviewView
          entries={visible.map(({ alert }) => alert)}
          startIndex={fullViewIdx}
          onClose={() => setFullViewIdx(null)}
          onVerdict={saveVerdictFullView}
          getEv={a => a.telemetry || {}}
          getId={a => a.filename}
          getCam={a => parseName(a.filename).cam || a.camera || '—'}
          getTs={a => a.filename}
          fmtTs={fn => { const m = parseName(fn); return `${m.date} ${m.time}`; }}
          imageUrlFor={a => `/alerts/${encodeURIComponent(a.filename)}/image`}
          // The alert photo keeps the FIRST qualifying frame of a visit, so it can be
          // well out of date by the time the rule fires — a car was labelled from a
          // photo 15.6s stale. These are the sidecar frames written next to the clip,
          // which until now only face enrolment ever read.
          // ReviewView defaults these to the blocked-record routes; point them at the
          // alert ones or every alert would fetch a missed-alert URL and 404.
          faceUrlFor={a => `/alerts/faces/${encodeURIComponent(a.filename)}_face.jpg`}
          stripCountFor={a => `/alerts/${encodeURIComponent(a.filename)}/frames`}
          stripFrameFor={a => i => `/alerts/${encodeURIComponent(a.filename)}/frame/${i}`}
          getSubtitle={a => a.verdict || 'unlabelled'}
          subtitleLabel="verdict"
          reasonFor={a => a.false_reason || null}
          verdicts={ALERT_VERDICTS}
          reasons={a => (isFaceCoverAlert(a) ? FC_REASONS : FALSE_REASONS)}
        />
      )}

      <div className="page-toolbar" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          className="alert-inline-input"
          style={{ flex: 1, minWidth: 0, padding: '8px 10px', fontSize: 13 }}
          placeholder="Paste alert ID to jump to it…"
          value={idQuery}
          onChange={e => setIdQuery(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') jumpToId(e.target.value); }}
        />
        <button className="tb-btn" style={{ flexShrink: 0 }} onClick={() => jumpToId(idQuery)}>Go</button>
        {idQuery && (
          <button className="tb-btn" style={{ flexShrink: 0 }} onClick={() => setIdQuery('')} title="Clear ID filter">✕</button>
        )}
      </div>

      {isMobile ? (
        <>
          <div className="page-toolbar mob-toolbar">
            <button className={`tb-btn${mediaMode === 'image' ? ' active' : ''}`} onClick={() => setMediaMode('image')}>📷 Image</button>
            <button className={`tb-btn${mediaMode === 'video' ? ' active' : ''}`} onClick={() => setMediaMode('video')}>🎬 Video</button>
            <MobilePickerField
              compact
              label="Camera"
              value={camFilter}
              onChange={setCamFilter}
              options={[{ value: null, label: '📷 All Cameras' }, ...cameras.map(c => ({ value: c.slug, label: c.name }))]}
            />
          </div>
          <div className="page-toolbar mob-toolbar" style={{ borderTop: '1px solid var(--border)' }}>
            <span className="mob-count-chip">
              {filter === 'all' ? `All (${total})` : `${TYPE_META[filter]?.label || filter} (${visible.length})`}
            </span>
            <div style={{ flex:1 }} />
            {/* Moved out of the scrollable pills row above (PRVSK-19) — that row
                is flex-nowrap + overflow-x:auto, so this could end up scrolled
                off-screen with no visual hint. This row never scrolls. */}
            <button className={`tb-btn mob-icon-btn${isMoreFiltered ? ' active' : ''}`} style={{ flexShrink: 0 }} onClick={() => setMobileSheetOpen(true)} title="More filters">
              🔍{isMoreFiltered ? ' ●' : ''}
            </button>
            {selected.size > 0 ? (
              <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                <BulkMarkMenu count={selected.size} onMark={markSelected} {...bulkReasonProps} />
                <button className="tb-btn" style={{ color:'#e74c3c', borderColor:'#e74c3c', flexShrink:0 }} onClick={deleteSelected}>
                  🗑 ({selected.size})
                </button>
              </div>
            ) : (
              <>
                <button className="tb-ghost-btn mob-icon-btn mob-icon-btn--refresh" onClick={() => loadPage(0, true)} title="Refresh" aria-label="Refresh">
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 4 23 10 17 10"/>
                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                  </svg>
                </button>
                <button className="tb-ghost-btn mob-icon-btn" onClick={() => setMobileMenuOpen(true)} aria-label="More options">
                  <svg width="4" height="18" viewBox="0 0 4 18" fill="currentColor">
                    <circle cx="2" cy="2" r="2"/><circle cx="2" cy="9" r="2"/><circle cx="2" cy="16" r="2"/>
                  </svg>
                </button>
              </>
            )}
          </div>

          {mobileSheetOpen && (
            <div className="dash-sheet-overlay" onClick={() => setMobileSheetOpen(false)}>
              <div className="dash-sheet" onClick={e => e.stopPropagation()}>
                <div className="dash-sheet-handle" />
                <h3>More Filters</h3>
                <div style={{ display:'flex', flexDirection:'column', gap:12, margin:'14px 0 18px' }}>
                  <div style={{ display:'flex', gap:10 }}>
                    <label className="mob-date-field">
                      <span className="mob-field-label">From</span>
                      <input className="mob-date-input" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                    </label>
                    <label className="mob-date-field">
                      <span className="mob-field-label">To</span>
                      <input className="mob-date-input" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
                    </label>
                  </div>
                  <div style={{ display:'flex', gap:10 }}>
                    <label className="mob-date-field">
                      <span className="mob-field-label">From time</span>
                      <input className="mob-date-input" type="time" value={timeFrom} onChange={e => setTimeFrom(e.target.value)} />
                    </label>
                    <label className="mob-date-field">
                      <span className="mob-field-label">To time</span>
                      <input className="mob-date-input" type="time" value={timeTo} onChange={e => setTimeTo(e.target.value)} />
                    </label>
                  </div>
                  <MobilePickerField
                    label="Type"
                    value={filter}
                    onChange={setFilter}
                    options={[{ value: 'all', label: `All (${total})` },
                      ...Object.entries(TYPE_META).map(([k, v]) => ({ value: k, label: v.label }))]}
                  />
                  <MobilePickerField
                    label="Verdict"
                    value={verdictFilter}
                    onChange={setVerdictFilter}
                    options={[
                      { value: 'all', label: 'All Verdicts' },
                      { value: 'unlabelled', label: 'Unlabelled' },
                      { value: 'correct', label: '✓ Correct' },
                      { value: 'false_alert', label: '✗ False Alert' },
                      { value: 'wrong_unknown', label: '⚠ Wrong ID' },
                    ]}
                  />
                  <MobilePickerField
                    label="Doorbell"
                    value={bellFilter ? 'rang' : 'all'}
                    onChange={v => setBellFilter(v === 'rang')}
                    options={[
                      { value: 'all', label: 'All alerts' },
                      { value: 'rang', label: '🔔 Doorbell only' },
                    ]}
                  />
                </div>
                <div style={{ display:'flex', gap:10 }}>
                  {isMoreFiltered && (
                    <button className="dash-sheet-close" style={{ flex:1 }}
                      onClick={() => { resetFilters(); setMobileSheetOpen(false); }}>✕ Reset</button>
                  )}
                  <button className="dash-sheet-close" style={{ flex:1, background:'var(--primary)', color:'#fff', borderColor:'var(--primary)' }}
                    onClick={() => setMobileSheetOpen(false)}>Apply</button>
                </div>
              </div>
            </div>
          )}

          {mobileMenuOpen && (
            <div className="dash-sheet-overlay" onClick={() => setMobileMenuOpen(false)}>
              <div className="dash-sheet" onClick={e => e.stopPropagation()}>
                <div className="dash-sheet-handle" />
                <button className="dash-drawer-link" onClick={() => { selectAll(); setMobileMenuOpen(false); }}>
                  <span className="dash-drawer-icon">✓</span><span>Select All</span>
                </button>
                <button className="dash-drawer-link" onClick={() => { clearSelection(); setMobileMenuOpen(false); }}>
                  <span className="dash-drawer-icon">○</span><span>None</span>
                </button>
                <button className="dash-drawer-link" disabled={dlBusy}
                  onClick={() => { setMobileMenuOpen(false); downloadLogs(fileVisible.map(v => v.alert.filename)); }}>
                  <span className="dash-drawer-icon">⬇</span><span>Download All Logs</span>
                </button>
                <button className="dash-drawer-link" style={{ color:'#e74c3c' }}
                  onClick={() => { setMobileMenuOpen(false); deleteAll(); }}>
                  <span className="dash-drawer-icon">🗑</span><span>Delete All</span>
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="page-toolbar">
            {/* Always-visible row */}
            <button className={`tb-btn${showFilters ? ' active' : ''}`}
              onClick={() => setShowFilters(o => !o)}
              title="Filters">
              🔍 Filters {isFiltered ? '●' : ''}
            </button>
            <button className="tb-btn" disabled={!visible.length}
              onClick={() => {
                const first = visible.findIndex(({ alert }) => !alert.verdict);
                setFullViewIdx(first >= 0 ? first : 0);
              }}
              title="Review these one at a time, with the rule trace">
              ⛶ Review
            </button>
            {isFiltered && (
              <button className="tb-ghost-btn" onClick={resetFilters} title="Clear all filters"
                style={{ color: 'var(--primary)' }}>
                ✕ Reset
              </button>
            )}
            <div className="tb-sep" />
            <span style={{ fontSize:13, color:'var(--text-muted)' }}>
              {filter === 'all' ? `All (${total})` : `${TYPE_META[filter]?.label || filter} (${visible.length})`}
            </span>
            <div className="tb-sep" />
            <button className="tb-ghost-btn" onClick={() => loadPage(0, true)}>↺ Refresh</button>
            <div className="tb-sep" />
            <button className="tb-ghost-btn" onClick={selectAll}>Select All</button>
            <button className="tb-ghost-btn" onClick={clearSelection}>None</button>
            <div className="tb-sep" />
            {selected.size > 0
              ? <button className="tb-btn" style={{ color: '#e74c3c', borderColor: '#e74c3c' }}
                  onClick={deleteSelected}>
                  🗑 Delete ({selected.size})
                </button>
              : <button className="tb-ghost-btn" style={{ color: '#e74c3c' }} onClick={deleteAll}>🗑 Delete All</button>
            }
            {selected.size > 0 && (
              <>
                <div className="tb-sep" />
                <BulkMarkMenu count={selected.size} onMark={markSelected} {...bulkReasonProps} />
              </>
            )}
            <div className="tb-sep" />
            {selected.size > 0
              ? <button className="tb-ghost-btn" disabled={dlBusy}
                  onClick={() => downloadLogs([...selected])}>
                  {dlBusy ? '⏳' : '⬇'} Download Logs ({selected.size})
                </button>
              : <button className="tb-ghost-btn" disabled={dlBusy}
                  onClick={() => downloadLogs(fileVisible.map(v => v.alert.filename))}>
                  {dlBusy ? '⏳' : '⬇'} Download All Logs ({visible.length})
                </button>
            }
            {/* Images for the same set the Logs button uses — selection if there is
                one, otherwise everything loaded. Keeping the two in step matters:
                a JSON export and an image export that cover different alerts are
                worse than useless as a training set. */}
            {selected.size > 0
              ? <button className="tb-ghost-btn" disabled={dlBusy}
                  title={selected.size > IMG_EXPORT_MAX
                         ? `Capped at ${IMG_EXPORT_MAX} — select fewer`
                         : 'Download the snapshots for the selected alerts as a zip'}
                  onClick={() => downloadImages([...selected])}>
                  {dlBusy ? '⏳' : '⬇'} Images ({selected.size})
                </button>
              : <button className="tb-ghost-btn" disabled={dlBusy}
                  title={visible.length > IMG_EXPORT_MAX
                         ? `Capped at ${IMG_EXPORT_MAX} — narrow the filter`
                         : 'Download the snapshots for these alerts as a zip'}
                  onClick={() => downloadImages(fileVisible.map(v => v.alert.filename))}>
                  {dlBusy ? '⏳' : '⬇'} Images ({visible.length})
                </button>
            }
          </div>

          {/* Collapsible filter row */}
          {showFilters && (
            <div className="page-toolbar" style={{ borderTop: '1px solid var(--border)', background:'var(--surface-alt, rgba(0,0,0,0.02))' }}>
              <button className={`tb-btn${mediaMode === 'image' ? ' active' : ''}`} onClick={() => setMediaMode('image')}>📷 Image</button>
              <button className={`tb-btn${mediaMode === 'video' ? ' active' : ''}`} onClick={() => setMediaMode('video')}>🎬 Video</button>
              <div className="tb-sep" />
              <span className="tb-label">From</span>
              <input className="tb-input" style={{borderRadius:6, paddingLeft:8}} type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
              <input className="tb-input" style={{borderRadius:6, paddingLeft:8}} type="time" value={timeFrom} onChange={e => setTimeFrom(e.target.value)} title="Start time (optional)" />
              <span className="tb-label">To</span>
              <input className="tb-input" style={{borderRadius:6, paddingLeft:8}} type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
              <input className="tb-input" style={{borderRadius:6, paddingLeft:8}} type="time" value={timeTo} onChange={e => setTimeTo(e.target.value)} title="End time (optional)" />
              <div className="tb-sep" />
              <AlertCamPicker cameras={cameras} value={camFilter} onChange={setCamFilter} />
              <div className="tb-sep" />
              <select className="tb-input" style={{ borderRadius:6, paddingLeft:8, width:'auto', cursor:'pointer' }}
                value={filter} onChange={e => setFilter(e.target.value)}>
                <option value="all">All ({total})</option>
                {Object.entries(TYPE_META).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
              <div className="tb-sep" />
              <select className="tb-input" style={{ borderRadius:6, paddingLeft:8, width:'auto', cursor:'pointer' }}
                value={verdictFilter} onChange={e => setVerdictFilter(e.target.value)}>
                <option value="all">All Verdicts</option>
                <option value="unlabelled">Unlabelled</option>
                <option value="correct">✓ Correct</option>
                <option value="false_alert">✗ False Alert</option>
                <option value="wrong_unknown">⚠ Wrong ID</option>
              </select>
              <div className="tb-sep" />
              <select className="tb-input" style={{ borderRadius:6, paddingLeft:8, width:'auto', cursor:'pointer' }}
                value={bellFilter ? 'rang' : 'all'} onChange={e => setBellFilter(e.target.value === 'rang')}
                title="Only the alerts that rang the doorbell">
                <option value="all">All alerts</option>
                <option value="rang">🔔 Doorbell only</option>
              </select>
            </div>
          )}
        </>
      )}

      {refreshing && (
        <div className="pk-loading" style={{ position: 'sticky', top: 0, zIndex: 101, padding: '12px 20px', background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
          ⏳ Loading alerts…
        </div>
      )}

      {alertBanner && (alertBanner.prepended ? (
        <div
          onClick={() => document.querySelector('main')?.scrollTo({ top: 0, behavior: 'smooth' })}
          style={{
            display: 'block', width: '100%', padding: '9px 16px',
            background: 'var(--banner-bg)', color: 'var(--banner-text)',
            fontSize: 13, fontWeight: 600, textAlign: 'center',
            position: 'sticky', top: 0, zIndex: 100, cursor: 'pointer',
          }}>
          ↑ {alertBanner.added} new alert{alertBanner.added !== 1 ? 's' : ''} added
        </div>
      ) : (
        <button
          onClick={() => {
            setAlertBanner(null);
            if (idleReloadTimerRef.current) { clearTimeout(idleReloadTimerRef.current); idleReloadTimerRef.current = null; }
            loadPage(0, true);
          }}
          style={{
            display: 'block', width: '100%', padding: '9px 16px',
            background: 'var(--banner-bg)', color: 'var(--banner-text)', border: 'none',
            cursor: 'pointer', fontSize: 13, fontWeight: 600, textAlign: 'center',
            position: 'sticky', top: 0, zIndex: 100,
          }}>
          {alertBanner.added > 0
            ? `${alertBanner.added} new alert${alertBanner.added !== 1 ? 's' : ''} — tap to refresh`
            : 'New alerts — tap to refresh'}
        </button>
      ))}

      {loading ? (
        <div className="pk-loading">⏳ Loading alerts…</div>
      ) : visible.length === 0 ? (
        <div className="pk-empty">
          {hasMore && filter !== 'all' ? (
            <>
              <div className="pk-empty-icon">🔍</div>
              <h3>Searching…</h3>
              <p>Loading more alerts of this type.</p>
            </>
          ) : (
            <>
              <div className="pk-empty-icon">🎉</div>
              <h3>No alerts</h3>
              <p>{filter !== 'all' ? 'No alerts for this type.' : bellFilter ? 'No doorbell alerts in this range.' : camFilter !== null ? 'No alerts for this camera in range.' : 'No alerts in this date range.'}</p>
            </>
          )}
        </div>
      ) : (
        <div className={`pk-grid${isMobile ? ' pk-grid-1col' : ''}`}>
          {visible.map(({ alert, originalIdx }) => {
            const meta    = parseName(alert.filename);
            const tm      = TYPE_META[meta.type] || { label: meta.type, cls: 'badge-instant' };
            const imgPath = `/alerts/${encodeURIComponent(alert.filename)}/image`;
            const vidPath = `/alerts/${encodeURIComponent(alert.filename)}/video`;
            const evtNum  = total - originalIdx;
            const alertId = alert.filename.replace(/\.(jpg|jpeg|png)$/i, '');

            if (alert.is_camera_offline) {
              const t = alert.ts || '';
              const when = t.length >= 15
                ? `${t.slice(6, 8)}/${t.slice(4, 6)}/${t.slice(0, 4)} ${t.slice(9, 11)}:${t.slice(11, 13)}:${t.slice(13, 15)}`
                : '';
              return (
                <div key={alert.filename} className="alert-card">
                  <div className="alert-card-num">#{evtNum}</div>
                  <div className="alert-card-thumb-placeholder alert-card-thumb" role="button" tabIndex={0}
                    title="Show which cameras are offline"
                    onClick={() => setCamOffModal(alert)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setCamOffModal(alert); }}
                    style={{ flexDirection: 'column', gap: 6, cursor: 'pointer', color: '#fff' }}>
                    <span style={{ fontSize: 40 }}>📴</span>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>{alert.label}</span>
                  </div>
                  <div className="alert-card-body">
                    <div className="alert-card-badges">
                      <span className="badge badge-instant">Camera offline</span>
                      {/* One camera: name it. Several: the label already says how
                          many, and the ℹ️ popup lists them — badges would crowd the card. */}
                      {alert.cameras?.length === 1 &&
                        <span className="badge badge-cam">{alert.cameras[0].cam_name}</span>}
                    </div>
                    <p className="alert-card-time">{when}</p>
                  </div>
                  <div className="alert-card-actions">
                    <button className="alert-action-btn" title="Offline details"
                      onClick={e => { e.stopPropagation(); setCamOffModal(alert); }}>ℹ️</button>
                  </div>
                </div>
              );
            }

            const isSelected = selected.has(alert.filename);
            return (
              <div
                key={alert.filename}
                className="alert-card"
                style={isSelected ? { outline: '2px solid #3498db', outlineOffset: 2 } : {}}
              >
                <div className="alert-card-num">#{evtNum}</div>
                <div
                  onClick={() => toggleSelect(alert.filename)}
                  style={{
                    position: 'absolute', top: 8, right: 8, zIndex: 10,
                    width: 20, height: 20, borderRadius: 4,
                    background: isSelected ? '#3498db' : 'rgba(0,0,0,0.45)',
                    border: `2px solid ${isSelected ? '#3498db' : 'rgba(255,255,255,0.5)'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0,
                  }}
                >{isSelected ? '✓' : ''}</div>

                <div style={{ display: mediaMode === 'image' ? 'block' : 'none', position: 'relative' }}>
                  <AuthImg
                    path={imgPath}
                    alt={alert.filename}
                    className="alert-card-thumb"
                    onClick={() => setLightbox({ path: imgPath, meta, idx: visible.findIndex(v => v.alert.filename === alert.filename) })}
                  />
                </div>
                <div style={{ display: mediaMode === 'video' ? 'block' : 'none' }}>
                  <AuthVideo path={vidPath} poster={imgPath} className="alert-card-thumb" />
                </div>
                <div className="alert-card-body">
                  <div className="alert-card-badges">
                    {prevSeenRef.current && stampFromFilename(alert.filename) > prevSeenRef.current &&
                      <span className="badge badge-new" title="Arrived since you last viewed alerts on this device">New</span>}
                    <span className={`badge ${tm.cls}`}>{tm.label}</span>
                    {meta.cam !== null && <span className="badge badge-cam">{cameras.find(c => c.slug === meta.cam)?.name || meta.cam}</span>}
                    {alert.devices_fired && <span className="badge badge-devices" title="Devices activated (siren / light / relay)">🔊 Devices</span>}
                    <VerdictBadge verdict={alert.verdict} />
                  </div>
                  <p className="alert-card-time">
                    {meta.date} {meta.time}
                    {alert.doorbell && <span className="alert-card-bell" title="This alert rang the doorbell" aria-label="Doorbell">🔔</span>}
                  </p>
                </div>
                <div className="alert-card-actions">
                  <button className="alert-action-btn" title="Play video"
                    onClick={e => { e.stopPropagation(); setVideoLightbox({ vidPath, filename: `${alertId}.mp4` }); }}><PlayIcon /></button>
                  {alert.has_face && (
                    <button className="alert-action-btn" title="Add to known faces"
                      style={{ borderColor: '#2ecc71', color: '#2ecc71' }}
                      onClick={e => { e.stopPropagation(); setFaceModal({ alertId, filename: alert.filename }); }}>
                      <span className="pk-icon-badge"><FaceIcon /><span className="pk-icon-badge-plus">+</span></span>
                    </button>
                  )}
                  <button className="alert-action-btn delete" title="Delete"
                    onClick={e => { e.stopPropagation(); deleteOne(alert.filename); }}><TrashIcon /></button>
                  <button className="alert-action-btn download" title="Download"
                    onClick={e => { e.stopPropagation(); handleDownloadMedia(alert, alertId, imgPath, vidPath); }}><DownloadIcon /></button>
                  <div className="pk-card-menu-wrap">
                    <button className="alert-action-btn" title="More options"
                      onClick={e => { e.stopPropagation(); setCardMenuOpen(cardMenuOpen === alert.filename ? null : alert.filename); }}><MoreIcon /></button>
                    {cardMenuOpen === alert.filename && (
                      <div className="pk-card-menu" onClick={e => e.stopPropagation()}>
                        <button className="pk-card-menu-item"
                          onClick={() => { setCardMenuOpen(null); handleShareMedia(alert, alertId, imgPath, vidPath); }}>
                          <ShareIcon /> Share
                        </button>
                        <button className="pk-card-menu-item"
                          onClick={() => { setCardMenuOpen(null); openAlertLog(alert.filename); }}>ℹ️ Alert info</button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && <div ref={sentinelRef} style={{ height: 1 }} />}
      {fetching && <div className="pk-loading" style={{ padding: '10px 0' }}>Loading more…</div>}

      {lightbox && mediaMode === 'image' && (
        <div className="pk-lightbox" onClick={() => setLightbox(null)}>
          <button className="pk-lightbox-close" onClick={() => setLightbox(null)}>✕</button>

          {/* Prev arrow */}
          {lightbox.idx > 0 && (
            <button onClick={e => { e.stopPropagation(); navigateLightbox(-1); }}
              style={{
                position: 'fixed', left: 16, top: '50%', transform: 'translateY(-50%)',
                background: 'rgba(0,0,0,0.55)', border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: '50%', width: 44, height: 44, color: '#fff', fontSize: 20,
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 10001, backdropFilter: 'blur(4px)',
              }}>‹</button>
          )}

          {/* Image */}
          {lbSrc
            ? <img src={lbSrc} alt="alert" className="pk-lightbox-img" onClick={e => e.stopPropagation()} />
            : <div style={{ color: '#666', fontSize: 14 }}>Loading…</div>
          }

          {/* Next arrow */}
          {lightbox.idx < visible.length - 1 && (
            <button onClick={e => { e.stopPropagation(); navigateLightbox(1); }}
              style={{
                position: 'fixed', right: 16, top: '50%', transform: 'translateY(-50%)',
                background: 'rgba(0,0,0,0.55)', border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: '50%', width: 44, height: 44, color: '#fff', fontSize: 20,
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 10001, backdropFilter: 'blur(4px)',
              }}>›</button>
          )}

          {/* Meta + counter */}
          <div className="pk-lightbox-meta" onClick={e => e.stopPropagation()}>
            {lightbox.meta && (() => {
              const tm = TYPE_META[lightbox.meta.type] || { label: lightbox.meta.type, cls: 'badge-instant' };
              return (
                <>
                  <span className={`badge ${tm.cls}`}>{tm.label}</span>
                  {lightbox.meta.cam !== null && <span className="badge badge-cam">{cameras.find(c => c.slug === lightbox.meta.cam)?.name || lightbox.meta.cam}</span>}
                  <span style={{ fontSize:12, color:'#aaa' }}>{lightbox.meta.date} {lightbox.meta.time}</span>
                  <span style={{ fontSize:11, color:'#666', marginLeft: 8 }}>{lightbox.idx + 1} / {visible.length}</span>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {videoLightbox && createPortal(
        <div className="pk-lightbox" style={{ position: 'absolute', pointerEvents: 'auto', background: '#000' }} onClick={() => setVideoLightbox(null)}>
          <button className="pk-lightbox-close" style={{ right: 68 }} disabled={downloadingClip}
            onClick={e => { e.stopPropagation(); handleDownloadClip(); }} title="Download video">
            {downloadingClip ? '⏳' : <DownloadIcon />}
          </button>
          <button className="pk-lightbox-close" onClick={() => setVideoLightbox(null)}>✕</button>
          <div onClick={e => e.stopPropagation()} style={{ width: 'min(720px, 90vw)', padding: '0 12px' }}>
            <AuthVideo path={videoLightbox.vidPath} className="pk-lightbox-img" style={{ width: '100%' }} autoPlay />
          </div>
        </div>,
        document.getElementById('lightbox-root')
      )}

      {camOffModal && <CamOfflineModal alert={camOffModal} onClose={() => setCamOffModal(null)} />}

      {actionModal && (
        <ActionModal
          filename={actionModal.filename}
          mode={actionModal.mode}
          onClose={() => setActionModal(null)}
          onDone={(msg) => { setActionModal(null); showToast(msg); }}
        />
      )}

      {faceModal && (
        <FacePreviewModal
          alertId={faceModal.alertId}
          filename={faceModal.filename}
          onClose={() => setFaceModal(null)}
          onAddKnown={() => { setFaceModal(null); setActionModal({ filename: faceModal.filename, mode: 'known' }); }}
          onAddBan={() => { setFaceModal(null); setActionModal({ filename: faceModal.filename, mode: 'ban' }); }}
        />
      )}

      {logModal && (
        <AlertLogModal
          data={logModal.data}
          filename={logModal.filename}
          onClose={() => setLogModal(null)}
        />
      )}

      <Toast msg={toast?.msg} type={toast?.type} />

      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          message={confirm.message}
          confirmLabel="Delete"
          confirmClass="danger"
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
